<template>
  <header class="entra-page-header">
    <div class="header-top">
      <div class="header-left">
        <div v-if="showBreadcrumb" class="breadcrumb">
          <router-link to="/entra/portfolio" class="breadcrumb-link">Portfolio</router-link>
          <span class="breadcrumb-sep">/</span>
          <span class="breadcrumb-current">{{ tenantDisplayName }}</span>
        </div>
        <h1 class="page-title">{{ title }}</h1>
      </div>
      <div class="header-right">
        <div v-if="showSearch" class="search-box">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" width="16" height="16">
            <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
          </svg>
          <input type="text" :placeholder="searchPlaceholder" :value="searchValue" @input="onSearch" class="search-input" />
        </div>
        <SavedViewMenu v-if="showSavedViews" :views="savedViews" @select-view="onSelectView" @save-view="onSaveView" />
        <button v-if="showRefresh" class="refresh-btn" @click="onRefresh" title="Request collection refresh">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" width="16" height="16">
            <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
          </svg>
          Refresh
        </button>
        <ExportMenu v-if="showExport" :formats="exportFormats" @export="onExport" />
      </div>
    </div>

    <div v-if="tenantDisplayName && showTenantInfo" class="header-info">
      <div class="info-items">
        <span class="info-item">
          <span class="info-label">Tenant:</span>
          <span class="info-value">{{ tenantDisplayName }}</span>
        </span>
        <span v-if="primaryDomain" class="info-item">
          <span class="info-label">Domain:</span>
          <span class="info-value">{{ primaryDomain }}</span>
        </span>
        <span v-if="regionCode" class="info-item">
          <span class="info-label">Region:</span>
          <span class="info-value region-badge">{{ regionCode }}</span>
        </span>
        <span v-if="dataCompleteness !== null" class="info-item">
          <span class="info-label">Completeness:</span>
          <span class="info-value">{{ formatPercentage(dataCompleteness) }}</span>
        </span>
      </div>
      <div class="info-right">
        <DataFreshnessChip :state="freshnessState" :collectedAt="lastCollection" />
        <span v-if="tenantStatus" class="tenant-status" :style="{ color: statusColor }">{{ statusLabel }}</span>
      </div>
    </div>

    <div v-if="capabilities && capabilities.length > 0" class="capability-banner" :class="'capability-banner-' + worstCapabilityState">
      <span class="capability-title">Capabilities:</span>
      <div class="capability-items">
        <span v-for="cap in capabilities" :key="cap.key" class="capability-item" :class="'capability-' + cap.state">
          {{ cap.key }}: {{ cap.stateLabel }}
        </span>
      </div>
    </div>
  </header>
</template>

<script>
import DataFreshnessChip from './DataFreshnessChip.vue';
import ExportMenu from './ExportMenu.vue';
import SavedViewMenu from './SavedViewMenu.vue';
import { getCapabilityStateLabel, getTenantStatusColor, getCollectionStatusLabel, formatPercentage } from '@/services/entra-utils.js';

export default {
  name: 'EntraPageHeader',
  components: { DataFreshnessChip, ExportMenu, SavedViewMenu },
  props: {
    title: { type: String, required: true },
    tenantDisplayName: { type: String, default: '' },
    primaryDomain: { type: String, default: '' },
    regionCode: { type: String, default: '' },
    tenantStatus: { type: String, default: '' },
    freshnessState: { type: String, default: 'fresh' },
    lastCollection: { type: String, default: '' },
    dataCompleteness: { type: Number, default: null },
    capabilities: { type: Array, default: function() { return []; } },
    showSearch: { type: Boolean, default: true },
    showExport: { type: Boolean, default: true },
    showRefresh: { type: Boolean, default: true },
    showSavedViews: { type: Boolean, default: true },
    showTenantInfo: { type: Boolean, default: true },
    showBreadcrumb: { type: Boolean, default: true },
    searchPlaceholder: { type: String, default: 'Search...' },
    savedViews: { type: Array, default: function() { return []; } },
    exportFormats: { type: Array, default: function() { return [{ key: 'csv', label: 'CSV' }, { key: 'json', label: 'JSON' }]; } },
    searchValue: { type: String, default: '' }
  },
  computed: {
    statusLabel: function() { return getCollectionStatusLabel(this.tenantStatus); },
    statusColor: function() { return getTenantStatusColor(this.tenantStatus); },
    worstCapabilityState: function() {
      var worst = 'available';
      var order = { failed: 5, 'missing-permission': 4, 'missing-licence': 3, 'collection-failed': 3, 'access-denied': 3, 'consent-expired': 2, 'unsupported-cloud': 1, 'not-configured': 1, unknown: 0, available: 0 };
      this.capabilities.forEach(function(c) {
        if ((order[c.state] || 0) > order[worst]) worst = c.state;
      });
      return worst;
    }
  },
  methods: {
    formatPercentage: formatPercentage,
    getCapabilityStateLabel: getCapabilityStateLabel,
    onSearch: function(e) { this.$emit('update:searchValue', e.target.value); this.$emit('search', e.target.value); },
    onRefresh: function() { this.$emit('refresh'); },
    onExport: function(format) { this.$emit('export', format); },
    onSelectView: function(key) { this.$emit('select-view', key); },
    onSaveView: function() { this.$emit('save-view'); }
  }
};
</script>

<style scoped>
.entra-page-header {
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  padding: 20px 24px;
}

.header-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 0;
}

.header-left {
  min-width: 0;
}

.breadcrumb {
  font-size: 0.75rem;
  color: #94a3b8;
  margin-bottom: 4px;
}
.breadcrumb-link { color: #3b82f6; text-decoration: none; }
.breadcrumb-link:hover { text-decoration: underline; }
.breadcrumb-sep { margin: 0 4px; }
.breadcrumb-current { color: #475569; font-weight: 500; }

.page-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.search-box {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 6px 12px;
}
.search-box svg { color: #94a3b8; flex-shrink: 0; }
.search-input {
  border: none;
  background: none;
  outline: none;
  font-size: 0.8125rem;
  color: #334155;
  width: 180px;
  font-family: 'Inter', sans-serif;
}
.search-input::placeholder { color: #94a3b8; }

.refresh-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  color: #475569;
  border: 1px solid #cbd5e1;
  padding: 6px 14px;
  border-radius: 6px;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  font-family: 'Inter', sans-serif;
}
.refresh-btn:hover { background: #f1f5f9; border-color: #94a3b8; }

.header-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f1f5f9;
}

.info-items {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 4px;
}

.info-label {
  font-size: 0.6875rem;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
}

.info-value {
  font-size: 0.8125rem;
  color: #334155;
  font-weight: 500;
}

.region-badge {
  background: #eff6ff;
  color: #2563eb;
  padding: 1px 8px;
  border-radius: 4px;
  font-weight: 700;
}

.info-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.tenant-status {
  font-size: 0.75rem;
  font-weight: 600;
}

.capability-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 0.75rem;
  flex-wrap: wrap;
}

.capability-banner-available { background: #f0fdf4; border: 1px solid #bbf7d0; }
.capability-banner-failed,
.capability-banner-missing-permission,
.capability-banner-collection-failed,
.capability-banner-access-denied { background: #fef2f2; border: 1px solid #fecaca; }
.capability-banner-delayed,
.capability-banner-missing-licence,
.capability-banner-consent-expired { background: #fefce8; border: 1px solid #fde047; }

.capability-title {
  font-weight: 600;
  color: #475569;
  flex-shrink: 0;
}

.capability-items {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.capability-item {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.6875rem;
  font-weight: 500;
}

.capability-available { background: #dcfce7; color: #166534; }
.capability-missing-permission,
.capability-missing-licence,
.capability-collection-failed,
.capability-access-denied { background: #fee2e2; color: #991b1b; }
.capability-consent-expired,
.capability-unsupported-cloud { background: #fef3c7; color: #92400e; }
.capability-not-configured,
.capability-unknown { background: #f1f5f9; color: #475569; }
</style>
